import React, { useEffect, useState } from 'react';
import axios from 'axios';
import {
  Brain,
  FloppyDisk,
  IdentificationCard,
  ChatCircleDots,
  CurrencyDollar,
  ArrowsLeftRight,
  WarningCircle,
  CaretDown,
  CaretUp,
} from '@phosphor-icons/react';
import { toast } from 'sonner';

const API = '/api';

const Section = ({
  title,
  subtitle,
  icon: Icon,
  open,
  onToggle,
  children,
}) => (
  <div className="border border-slate-200 rounded-xl overflow-hidden">
    <button
      type="button"
      onClick={onToggle}
      className="w-full flex items-center justify-between gap-4 p-5 bg-white hover:bg-slate-50 text-left"
    >
      <div className="flex items-center gap-3">
        <div className="p-2 rounded-lg bg-purple-100 text-purple-700">
          <Icon size={21} />
        </div>

        <div>
          <p className="font-bold text-slate-900">
            {title}
          </p>

          {subtitle && (
            <p className="text-sm text-slate-500 mt-1">
              {subtitle}
            </p>
          )}
        </div>
      </div>

      {open ? (
        <CaretUp size={20} />
      ) : (
        <CaretDown size={20} />
      )}
    </button>

    {open && (
      <div className="p-5 border-t border-slate-200 bg-slate-50">
        {children}
      </div>
    )}
  </div>
);

const Field = ({
  label,
  helper,
  children,
}) => (
  <div>
    <label className="block text-sm font-semibold text-slate-700 mb-2">
      {label}
    </label>

    {children}

    {helper && (
      <p className="text-xs text-slate-500 mt-2">
        {helper}
      </p>
    )}
  </div>
);

const TextArea = ({
  value,
  onChange,
  rows = 4,
}) => (
  <textarea
    value={value || ''}
    onChange={(event) =>
      onChange(event.target.value)
    }
    rows={rows}
    className="w-full border border-slate-300 rounded-xl px-4 py-3 bg-white focus:outline-none focus:ring-2 focus:ring-purple-400"
  />
);

const Toggle = ({
  checked,
  onChange,
  title,
  description,
}) => (
  <label className="flex items-start justify-between gap-4 p-4 rounded-xl bg-white border border-slate-200">
    <div>
      <p className="font-semibold text-slate-900">
        {title}
      </p>

      {description && (
        <p className="text-sm text-slate-500 mt-1">
          {description}
        </p>
      )}
    </div>

    <input
      type="checkbox"
      checked={Boolean(checked)}
      onChange={(event) =>
        onChange(event.target.checked)
      }
      className="mt-1 w-5 h-5"
    />
  </label>
);

const SalesDNASettings = () => {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [advancedMode, setAdvancedMode] = useState(false);

  const [open, setOpen] = useState({
    identity: true,
    principles: false,
    policies: false,
    objections: false,
    handoff: false,
  });

  const fetchConfig = async () => {
    try {
      const { data } = await axios.get(
        `${API}/ai/sales-dna`,
        {
          withCredentials: true,
        }
      );

      setConfig(data);
    } catch (error) {
      if (error?.response?.status !== 403) {
        console.error(error);
        toast.error(
          'Erro ao carregar o Sales DNA'
        );
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchConfig();
  }, []);

  const updateSection = (
    section,
    field,
    value
  ) => {
    setConfig((current) => ({
      ...current,
      [section]: {
        ...(current?.[section] || {}),
        [field]: value,
      },
    }));
  };

  const updatePolicy = (
    policy,
    field,
    value
  ) => {
    setConfig((current) => ({
      ...current,
      acquisition_policies: {
        ...(current?.acquisition_policies || {}),
        [policy]: {
          ...(current?.acquisition_policies?.[
            policy
          ] || {}),
          [field]: value,
        },
      },
    }));
  };

  const updateObjection = (
    objection,
    field,
    value
  ) => {
    setConfig((current) => ({
      ...current,
      objections: {
        ...(current?.objections || {}),
        [objection]: {
          ...(current?.objections?.[
            objection
          ] || {}),
          [field]: value,
        },
      },
    }));
  };

  const toggleAdvancedMode = () => {
    if (advancedMode) {
      setAdvancedMode(false);

      setOpen((current) => ({
        ...current,
        principles: false,
        objections: false,
        handoff: false,
      }));

      toast.success(
        'Configurações avançadas bloqueadas'
      );

      return;
    }

    const confirmed = window.confirm(
      'As configurações avançadas alteram regras estruturais ' +
      'do comportamento comercial da IA. ' +
      'Deseja liberar a edição nesta sessão?'
    );

    if (!confirmed) return;

    setAdvancedMode(true);

    toast.success(
      'Configurações avançadas liberadas nesta sessão'
    );
  };

  const toggleProtectedSection = (section) => {
    if (!advancedMode) {
      toast(
        'Área protegida. Clique em Configurações avançadas para liberar.'
      );

      return;
    }

    setOpen((current) => ({
      ...current,
      [section]: !current[section],
    }));
  };

  const save = async () => {
    if (!config) return;

    setSaving(true);

    try {
      const { data } = await axios.put(
        `${API}/ai/sales-dna`,
        config,
        {
          withCredentials: true,
        }
      );

      setConfig(data);

      toast.success(
        'Sales DNA v1 salvo com sucesso'
      );
    } catch (error) {
      console.error(error);

      toast.error(
        error?.response?.data?.detail ||
        'Erro ao salvar Sales DNA'
      );
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-white border border-slate-200 rounded-xl p-6">
        <p className="text-slate-500">
          Carregando Sales DNA...
        </p>
      </div>
    );
  }

  if (!config) return null;

  const identity = config.identity || {};
  const principles =
    config.conversation_principles || {};
  const policies =
    config.acquisition_policies || {};
  const handoff = config.handoff || {};

  return (
    <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
      <div className="p-6 border-b border-slate-200 flex flex-col xl:flex-row xl:items-center justify-between gap-5">
        <div className="flex items-start gap-4">
          <div className="p-3 rounded-xl bg-purple-100 text-purple-700">
            <Brain
              size={28}
              weight="duotone"
            />
          </div>

          <div>
            <div className="flex flex-wrap items-center gap-3">
              <h2 className="text-xl font-bold text-slate-900">
                ORKESTRY Sales DNA v1
              </h2>

              <span className="px-2.5 py-1 rounded-full bg-amber-100 text-amber-700 text-xs font-bold">
                CONFIGURAÇÃO
              </span>

              <span className="px-2.5 py-1 rounded-full bg-slate-100 text-slate-600 text-xs font-bold">
                RUNTIME DESATIVADO
              </span>
            </div>

            <p className="text-sm text-slate-600 mt-2">
              Personalidade comercial, políticas,
              objeções e regras de continuidade.
            </p>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <button
            type="button"
            onClick={toggleAdvancedMode}
            className={
              advancedMode
                ? 'inline-flex items-center justify-center gap-2 px-4 py-3 rounded-xl border border-amber-300 bg-amber-50 text-amber-800 font-semibold hover:bg-amber-100'
                : 'inline-flex items-center justify-center gap-2 px-4 py-3 rounded-xl border border-slate-300 bg-white text-slate-700 font-semibold hover:bg-slate-50'
            }
          >
            <span aria-hidden="true">
              {advancedMode ? '🔓' : '⚙️'}
            </span>

            {advancedMode
              ? 'Bloquear configurações'
              : 'Configurações avançadas'}
          </button>

          <button
            type="button"
            onClick={save}
            disabled={saving}
            className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-xl bg-purple-600 text-white font-semibold hover:bg-purple-700 disabled:opacity-50"
          >
            <FloppyDisk size={20} />

            {saving
              ? 'Salvando...'
              : 'Salvar Sales DNA'}
          </button>
        </div>
      </div>

      <div className="p-5 bg-amber-50 border-b border-amber-200 flex gap-3">
        <WarningCircle
          size={22}
          className="text-amber-700 shrink-0"
        />

        <div>
          <p className="font-semibold text-amber-900">
            Etapa de configuração segura
          </p>

          <p className="text-sm text-amber-800 mt-1">
            Estas regras ainda não estão conectadas
            ao atendimento real. Primeiro validaremos
            os dados, depois ativaremos o Sales DNA
            progressivamente no motor de conversa.
          </p>
        </div>
      </div>

      <div className="p-5 space-y-4">
        <Section
          title="Identidade do atendimento"
          subtitle="Nome, apresentação e posicionamento da assistente."
          icon={IdentificationCard}
          open={open.identity}
          onToggle={() =>
            setOpen((current) => ({
              ...current,
              identity: !current.identity,
            }))
          }
        >
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            <Field label="Nome da assistente">
              <input
                value={
                  identity.assistant_name || ''
                }
                onChange={(event) =>
                  updateSection(
                    'identity',
                    'assistant_name',
                    event.target.value
                  )
                }
                className="input-default"
                placeholder="Akerzy"
              />
            </Field>

            <Field label="Time">
              <input
                value={
                  identity.team_name || ''
                }
                onChange={(event) =>
                  updateSection(
                    'identity',
                    'team_name',
                    event.target.value
                  )
                }
                className="input-default"
              />
            </Field>

            <Field label="Empresa">
              <input
                value={
                  identity.company_name || ''
                }
                onChange={(event) =>
                  updateSection(
                    'identity',
                    'company_name',
                    event.target.value
                  )
                }
                className="input-default"
              />
            </Field>
          </div>

          <div className="mt-5">
            <Field
              label="Saudação inicial"
              helper="Variáveis disponíveis: {first_name}, {assistant_name}, {team_name}, {company_name}."
            >
              <TextArea
                value={
                  identity.opening_template
                }
                onChange={(value) =>
                  updateSection(
                    'identity',
                    'opening_template',
                    value
                  )
                }
                rows={4}
              />
            </Field>
          </div>
        </Section>

        <Section
          title={
            advancedMode
              ? 'Personalidade e comportamento'
              : '🔒 Personalidade e comportamento'
          }
          subtitle="Princípios que controlam como o atendimento se comporta."
          icon={ChatCircleDots}
          open={open.principles}
          onToggle={() =>
            toggleProtectedSection('principles')
          }
        >
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
            <Toggle
              checked={
                principles.generalist_until_specialist_needed
              }
              onChange={(value) =>
                updateSection(
                  'conversation_principles',
                  'generalist_until_specialist_needed',
                  value
                )
              }
              title="Atuar como generalista até a especialização"
            />

            <Toggle
              checked={
                principles.answer_question_before_resuming_flow
              }
              onChange={(value) =>
                updateSection(
                  'conversation_principles',
                  'answer_question_before_resuming_flow',
                  value
                )
              }
              title="Responder antes de retomar a qualificação"
            />

            <Toggle
              checked={
                principles.never_repeat_known_information
              }
              onChange={(value) =>
                updateSection(
                  'conversation_principles',
                  'never_repeat_known_information',
                  value
                )
              }
              title="Não perguntar novamente dados já conhecidos"
            />

            <Toggle
              checked={
                principles.adaptive_tone
              }
              onChange={(value) =>
                updateSection(
                  'conversation_principles',
                  'adaptive_tone',
                  value
                )
              }
              title="Tom adaptativo ao contexto"
            />

            <Toggle
              checked={
                principles.mirror_customer_informality
              }
              onChange={(value) =>
                updateSection(
                  'conversation_principles',
                  'mirror_customer_informality',
                  value
                )
              }
              title="Espelhar informalidade do cliente"
            />

            <Toggle
              checked={
                principles.never_invent_product_information
              }
              onChange={(value) =>
                updateSection(
                  'conversation_principles',
                  'never_invent_product_information',
                  value
                )
              }
              title="Nunca inventar informações comerciais"
            />

            <Toggle
              checked={
                principles.never_attack_competitors
              }
              onChange={(value) =>
                updateSection(
                  'conversation_principles',
                  'never_attack_competitors',
                  value
                )
              }
              title="Nunca desqualificar concorrentes"
            />

            <Toggle
              checked={
                principles.never_create_false_urgency
              }
              onChange={(value) =>
                updateSection(
                  'conversation_principles',
                  'never_create_false_urgency',
                  value
                )
              }
              title="Nunca criar urgência falsa"
            />

            <Toggle
              checked={
                principles.never_offer_transfer_as_question
              }
              onChange={(value) =>
                updateSection(
                  'conversation_principles',
                  'never_offer_transfer_as_question',
                  value
                )
              }
              title='Nunca perguntar "quer falar com um corretor?"'
            />
          </div>

          <div className="mt-5">
            <Field label="Estilo comercial">
              <TextArea
                value={
                  principles.commercial_style
                }
                onChange={(value) =>
                  updateSection(
                    'conversation_principles',
                    'commercial_style',
                    value
                  )
                }
              />
            </Field>
          </div>
        </Section>

        <Section
          title="Políticas comerciais"
          subtitle="Regras determinísticas; não devem depender de interpretação da IA."
          icon={CurrencyDollar}
          open={open.policies}
          onToggle={() =>
            setOpen((current) => ({
              ...current,
              policies: !current.policies,
            }))
          }
        >
          <div className="space-y-6">
            <div className="rounded-xl bg-white border border-slate-200 p-5">
              <h3 className="font-bold text-slate-900">
                VCA Facilita
              </h3>

              <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mt-4">
                <Field label="Entrada padrão (%)">
                  <input
                    type="number"
                    step="0.01"
                    value={
                      policies.vca_facilita
                        ?.default_down_payment_pct ??
                      10
                    }
                    onChange={(event) =>
                      updatePolicy(
                        'vca_facilita',
                        'default_down_payment_pct',
                        Number(event.target.value)
                      )
                    }
                    className="input-default"
                  />
                </Field>

                <Field label="Máximo de parcelas">
                  <input
                    type="number"
                    value={
                      policies.vca_facilita
                        ?.max_installments ??
                      100
                    }
                    onChange={(event) =>
                      updatePolicy(
                        'vca_facilita',
                        'max_installments',
                        Number(event.target.value)
                      )
                    }
                    className="input-default"
                  />
                </Field>

                <Field label="Correção durante obra">
                  <input
                    value={
                      policies.vca_facilita
                        ?.construction_adjustment_index ||
                      ''
                    }
                    onChange={(event) =>
                      updatePolicy(
                        'vca_facilita',
                        'construction_adjustment_index',
                        event.target.value
                      )
                    }
                    className="input-default"
                  />
                </Field>

                <Field label="Juros pós-obra (% a.m.)">
                  <input
                    type="number"
                    step="0.0001"
                    value={
                      policies.vca_facilita
                        ?.post_handover_monthly_rate_pct ??
                      0.7974
                    }
                    onChange={(event) =>
                      updatePolicy(
                        'vca_facilita',
                        'post_handover_monthly_rate_pct',
                        Number(event.target.value)
                      )
                    }
                    className="input-default"
                  />
                </Field>
              </div>

              <div className="mt-4">
                <Field label="Índice pós-obra">
                  <input
                    value={
                      policies.vca_facilita
                        ?.post_handover_adjustment_index ||
                      ''
                    }
                    onChange={(event) =>
                      updatePolicy(
                        'vca_facilita',
                        'post_handover_adjustment_index',
                        event.target.value
                      )
                    }
                    className="input-default"
                  />
                </Field>
              </div>
            </div>

            <div className="rounded-xl bg-white border border-slate-200 p-5">
              <h3 className="font-bold text-slate-900">
                Pagamento à vista
              </h3>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
                <Field label="Desconto padrão (%)">
                  <input
                    type="number"
                    step="0.01"
                    value={
                      policies.cash
                        ?.standard_discount_pct ??
                      5
                    }
                    onChange={(event) =>
                      updatePolicy(
                        'cash',
                        'standard_discount_pct',
                        Number(event.target.value)
                      )
                    }
                    className="input-default"
                  />
                </Field>
              </div>

              <div className="mt-4">
                <Field label="Condições fora do padrão">
                  <TextArea
                    value={
                      policies.cash
                        ?.outside_standard_rule ||
                      ''
                    }
                    onChange={(value) =>
                      updatePolicy(
                        'cash',
                        'outside_standard_rule',
                        value
                      )
                    }
                  />
                </Field>
              </div>
            </div>

            <div className="rounded-xl bg-white border border-slate-200 p-5">
              <h3 className="font-bold text-slate-900">
                Financiamento bancário
              </h3>

              <Field label="Estratégia">
                <TextArea
                  value={
                    policies.bank_financing
                      ?.strategy ||
                    ''
                  }
                  onChange={(value) =>
                    updatePolicy(
                      'bank_financing',
                      'strategy',
                      value
                    )
                  }
                />
              </Field>
            </div>
          </div>
        </Section>

        <Section
          title={
            advancedMode
              ? 'Objeções e situações especiais'
              : '🔒 Objeções e situações especiais'
          }
          subtitle="Objetivos comerciais; não são respostas fixas."
          icon={WarningCircle}
          open={open.objections}
          onToggle={() =>
            toggleProtectedSection('objections')
          }
        >
          <div className="space-y-5">
            {[
              ['price_high', 'Está caro'],
              ['no_down_payment', 'Não tenho entrada'],
              ['thinking', 'Vou pensar'],
              ['researching', 'Só estou pesquisando'],
              ['competitor_cheaper', 'Vi outro mais barato'],
              ['bad_location', 'Essa localização é ruim'],
            ].map(([key, label]) => (
              <div
                key={key}
                className="rounded-xl bg-white border border-slate-200 p-5"
              >
                <h3 className="font-bold text-slate-900 mb-4">
                  {label}
                </h3>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  <Field label="Objetivo comercial">
                    <TextArea
                      value={
                        config.objections?.[key]
                          ?.objective ||
                        ''
                      }
                      onChange={(value) =>
                        updateObjection(
                          key,
                          'objective',
                          value
                        )
                      }
                    />
                  </Field>

                  <Field label="Estratégia">
                    <TextArea
                      value={
                        config.objections?.[key]
                          ?.strategy ||
                        ''
                      }
                      onChange={(value) =>
                        updateObjection(
                          key,
                          'strategy',
                          value
                        )
                      }
                    />
                  </Field>
                </div>
              </div>
            ))}
          </div>
        </Section>

        <Section
          title={
            advancedMode
              ? 'Continuidade com especialista'
              : '🔒 Continuidade com especialista'
          }
          subtitle="Handoff sem ruptura de experiência."
          icon={ArrowsLeftRight}
          open={open.handoff}
          onToggle={() =>
            toggleProtectedSection('handoff')
          }
        >
          <div className="space-y-5">
            <Toggle
              checked={
                handoff.never_ask_permission_to_transfer
              }
              onChange={(value) =>
                updateSection(
                  'handoff',
                  'never_ask_permission_to_transfer',
                  value
                )
              }
              title="Não pedir autorização para transferir"
              description="A continuidade deve ser apresentada como especialização do atendimento."
            />

            <Field label="Mensagem de continuidade para o lead">
              <TextArea
                value={
                  handoff.default_transition_template
                }
                onChange={(value) =>
                  updateSection(
                    'handoff',
                    'default_transition_template',
                    value
                  )
                }
              />
            </Field>

            <Field label="Primeira mensagem sugerida ao especialista">
              <TextArea
                value={
                  handoff.specialist_first_message_template
                }
                onChange={(value) =>
                  updateSection(
                    'handoff',
                    'specialist_first_message_template',
                    value
                  )
                }
              />
            </Field>

            <Field label="Regra para proposta fora da condição padrão">
              <TextArea
                value={
                  handoff.outside_standard_terms_rule
                }
                onChange={(value) =>
                  updateSection(
                    'handoff',
                    'outside_standard_terms_rule',
                    value
                  )
                }
              />
            </Field>
          </div>
        </Section>
      </div>
    </div>
  );
};

export default SalesDNASettings;
